/**
 * YionaraAssistant — the production floating AI assistant.
 *
 * Mounted globally by App.tsx for every authenticated user. Wraps the
 * shared AIAssistantProvider (chat state, messaging, persona context)
 * around a composition of ChatWindow / ChatHeader / MessageList /
 * ChatInput / Sidebar / ToggleButton sub-components.
 *
 * Global open-and-send hook: any component can dispatch
 *   window.dispatchEvent(new CustomEvent('eis:open-agent', {
 *     detail: { message: '...' }
 *   }))
 * and this component will pop the chat open and auto-submit the
 * message. Used by the "Catch me up" dashboard card.
 *
 * File history: previously named AIAssistantRefactored.tsx. The
 * "refactored" suffix was transitional from when the monolithic
 * AIAssistant.tsx was split into composed sub-components. Renamed
 * to match the Yionara* brand prefix used by the persona agent family.
 */
import React, { useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { AIAssistantProvider, useAIAssistant } from './AIAssistantProvider';
import { AIAssistantProps } from './types';

// Inner component that consumes the context
import ChatWindow from './ChatWindow';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import ToggleButton from './ToggleButton';
import Sidebar from './layout/Sidebar';
import ErrorBoundary from './common/ErrorBoundary';

function YionaraAssistantInner({ className = '' }: { className?: string }) {
  const ctx = useAIAssistant();

  const handleMinimizeToggle = () => {
    ctx.setIsMinimized(prev => !prev);
  };

  // Global open-and-send hook. Any part of the app can dispatch:
  //   window.dispatchEvent(new CustomEvent('eis:open-agent', { detail: { message } }))
  // and the chat will pop open + auto-submit that message. Used by the
  // "Catch me up" dashboard card and any future one-click AI actions.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const message: string = typeof detail.message === 'string' ? detail.message : '';
      ctx.setIsOpen(true);
      ctx.setIsMinimized(false);
      if (!message) return;
      // Give the chat window and input a tick to mount before we push
      // the prompt through. setInputValue primes the textarea so the user
      // sees their request land before the send fires it.
      setTimeout(() => {
        ctx.setInputValue(message);
        setTimeout(() => {
          ctx.handleSendMessage(message);
        }, 80);
      }, 120);
    };
    window.addEventListener('eis:open-agent', handler as EventListener);
    return () => window.removeEventListener('eis:open-agent', handler as EventListener);
    // ctx methods are stable via provider; no deps needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ChatWindow
        isOpen={ctx.isOpen}
        onClose={() => ctx.setIsOpen(false)}
        theme={ctx.theme}
        className={className}
        isMinimized={ctx.isMinimized}
      >
        <ChatHeader
          onClose={() => ctx.setIsOpen(false)}
          onToggleTheme={() => ctx.setIsDarkMode(!ctx.isDarkMode)}
          onNewChat={ctx.handleNewChat}
          onMinimize={handleMinimizeToggle}
          isMinimized={ctx.isMinimized}
          theme={ctx.theme}
          title={ctx.personaConfig.assistantName}
          subtitle={ctx.personaConfig.assistantSubtitle}
          headerGradient={ctx.personaConfig.headerGradient}
          headerAccent={ctx.personaConfig.headerAccent}
          logoBg={ctx.personaConfig.logoBg}
        />

        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar — hidden when minimized or on smaller screens */}
          {!ctx.isMinimized && <Sidebar />}

          {/* Main content area */}
          <main className="flex-1 flex flex-col bg-white relative min-w-0">
            <MessageList
              messages={ctx.messages}
              theme={ctx.theme}
              welcomeMessage={ctx.welcomeMessage}
              suggestions={ctx.suggestions}
              pendingConfirmation={ctx.pendingConfirmation}
              isLoading={ctx.isLoading}
              isStreaming={ctx.isStreaming}
              statusMessage={ctx.statusMessage}
              onConfirmAction={ctx.handleConfirmAction}
              onCancelConfirmation={ctx.handleCancelConfirmation}
              onSuggestionClick={ctx.handleSuggestionClick}
              onEditMessage={ctx.handleEditMessage}
              onDismissUpdate={(messageId) => {
                ctx.setMessages(prev => prev.filter(m => m.id !== messageId));
              }}
            />
            <ChatInput
              ref={ctx.chatInputRef}
              value={ctx.inputValue}
              onChange={ctx.setInputValue}
              onSend={ctx.handleSendMessage}
              onStop={ctx.handleStopProcessing}
              onAddMessage={(role, content) => {
                ctx.setMessages(prev => [...prev, {
                  id: `${role}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
                  role,
                  content,
                  timestamp: new Date(),
                }]);
              }}
              statusMessage={ctx.statusMessage}
              disabled={ctx.isLoading || ctx.isStreaming}
              placeholder={ctx.personaConfig.inputPlaceholder}
              theme={ctx.theme}
              attachments={ctx.attachments}
              onRemoveAttachment={(id) => ctx.setAttachments(prev => prev.filter(a => a.id !== id))}
            />
          </main>
        </div>
      </ChatWindow>

      <ToggleButton
        isOpen={ctx.isOpen}
        onToggle={() => ctx.setIsOpen(!ctx.isOpen)}
        className={className}
        hasUnreadMessages={ctx.hasUnseenUpdates || ctx.hasUnseenChangeNotifications}
        accentColor={ctx.personaConfig.accentColor}
        accentColorHex={ctx.personaConfig.accentColorHex}
        iconLabel={ctx.personaConfig.iconLabel}
        toggleGlow={ctx.personaConfig.toggleGlow}
      />
    </>
  );
}

const YionaraAssistant: React.FC<AIAssistantProps> = ({ companyId, className = '' }) => {
  const { user } = useAuth();

  if (!user) {
    return (
      <div className={`fixed bottom-6 right-6 z-50 ${className}`}>
        <p className="text-sm text-gray-500">Loading user...</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AIAssistantProvider companyId={companyId}>
        <YionaraAssistantInner className={className} />
      </AIAssistantProvider>
    </ErrorBoundary>
  );
};

export default YionaraAssistant;
